import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Alert,
  Divider,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { useParams } from 'react-router-dom';
import contractService from '../services/contractService';
import SignaturePad from '../components/SignaturePad';

/**
 * ContractSign - Public page for signing contracts
 * No authentication required
 */
const ContractSign = () => {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signatureData, setSignatureData] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    loadContract();
  }, [token]);

  const loadContract = async () => {
    try {
      setLoading(true);
      const response = await contractService.getContractByToken(token);
      setContract(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load contract');
      console.error('Error loading contract:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureSave = (base64Data) => {
    setSignatureData(base64Data);
  };

  const handleSignatureClear = () => {
    setSignatureData(null);
  };

  const handleSignContract = async () => {
    if (!signatureData) {
      alert('Please sign the document first');
      return;
    }

    if (!window.confirm('By signing this contract, you agree to the terms and conditions outlined above. This signature is legally binding. Continue?')) {
      return;
    }

    try {
      setSigning(true);
      await contractService.signContract(contract.id, {
        signing_token: token,
        signature_data: signatureData
      });
      setSigned(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to sign contract');
      console.error('Error signing contract:', err);
    } finally {
      setSigning(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Unable to Load Contract
          </Typography>
          <Typography color="text.secondary">
            {error}
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (signed) {
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Contract Signed Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Thank you for signing the contract. A confirmation email has been sent to <strong>{contract.signer_email}</strong>.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You may now close this window.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" component="h1" gutterBottom>
            PaceEstate
          </Typography>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            Contract Signature Request
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Please review the contract below carefully before signing. Your electronic signature will be legally binding.
        </Alert>

        {/* Contract Information */}
        <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {contract.title}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Signer Name
                </Typography>
                <Typography variant="body1">{contract.signer_name}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Email
                </Typography>
                <Typography variant="body1">{contract.signer_email}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Contract Type
                </Typography>
                <Typography variant="body1">
                  {contract.contract_type.replace('_', ' ').toUpperCase()}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Contract Content */}
        <Paper variant="outlined" sx={{ p: 3, mb: 4, maxHeight: 500, overflow: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            Contract Terms
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              '& h1, & h2, & h3': { mt: 2, mb: 1 },
              '& p': { mb: 1 },
              '& ul, & ol': { pl: 3 }
            }}
            dangerouslySetInnerHTML={{ __html: contract.content }}
          />
        </Paper>

        {/* Signature Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Your Signature
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <SignaturePad
              onSave={handleSignatureSave}
              onClear={handleSignatureClear}
              width={600}
              height={200}
            />
          </Box>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Legal Notice:</strong> By clicking "Sign Contract" below, you acknowledge that you have read,
            understood, and agree to the terms outlined in this contract. Your electronic signature is legally
            binding and will be recorded along with your IP address and timestamp.
          </Alert>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSignContract}
              disabled={!signatureData || signing}
              startIcon={signing ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              sx={{ minWidth: 200 }}
            >
              {signing ? 'Signing...' : 'Sign Contract'}
            </Button>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0', textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Powered by PaceEstate Estate Sales Management Platform
          </Typography>
          <Typography variant="caption" color="text.secondary">
            If you have questions about this contract, please contact us.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default ContractSign;
